import { Badge } from "@/core/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/core/components/ui/table";
import { formatMinorAmount } from "@/core/utils/func/money-minor";
import type { JournalEntryDto } from "@/features/ledger/api/dto";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import type { JournalEntryStatus } from "@/features/shared/api/enums";

const STATUS_VARIANTS: Record<
  JournalEntryStatus,
  "warning" | "success" | "destructive" | "secondary"
> = {
  PENDING: "warning",
  POSTED: "success",
  REJECTED: "destructive",
  REVERSED: "secondary",
};

/** Champ libellé/valeur du détail — données financières en `NoTranslate`. */
function DetailField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <NoTranslate as="span" className="block text-sm font-medium break-all">
        {value || "—"}
      </NoTranslate>
    </div>
  );
}

/**
 * Carte de détail d'une écriture comptable (`GET /JournalEntries/{id}`) —
 * en-tête (référence, produit, source, statut, date de valeur) et lignes
 * débit/crédit (montants en unité mineure formatés).
 */
export function JournalEntryDetailCard({ entry }: { entry: JournalEntryDto }) {
  const { t } = useLedgerTranslation();

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("ledger.journal.detail.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField
            label={t("ledger.journal.columns.reference")}
            value={entry.reference}
          />
          <DetailField
            label={t("ledger.journal.columns.productCode")}
            value={entry.productCode}
          />
          <DetailField
            label={t("ledger.journal.detail.providerCode")}
            value={entry.providerCode}
          />
          <DetailField
            label={t("ledger.journal.columns.sourceService")}
            value={entry.sourceService}
          />
          <DetailField
            label={t("ledger.journal.columns.sourceTransactionRef")}
            value={entry.sourceTransactionRef}
          />
          <DetailField
            label={t("ledger.journal.detail.reversalOfEntryId")}
            value={entry.reversalOfEntryId}
          />
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">
              {t("ledger.journal.columns.status")}
            </p>
            {entry.status ? (
              <NoTranslate as="span">
                <Badge variant={STATUS_VARIANTS[entry.status] ?? "outline"}>
                  {entry.status}
                </Badge>
              </NoTranslate>
            ) : (
              "—"
            )}
          </div>
          <DetailField
            label={t("ledger.journal.columns.currency")}
            value={entry.currency}
          />
          <DetailField
            label={t("ledger.journal.columns.businessDate")}
            value={entry.businessDate}
          />
          <DetailField
            label={t("ledger.journal.detail.createdAt")}
            value={entry.createdAt}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("ledger.journal.detail.lines")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("ledger.journal.detail.accountCode")}</TableHead>
                <TableHead>{t("ledger.journal.detail.direction")}</TableHead>
                <TableHead className="text-right">
                  {t("ledger.journal.detail.amount")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(entry.lines ?? []).map((line) => (
                <TableRow key={line.id ?? `${line.accountId}-${line.direction}`}>
                  <TableCell>
                    <NoTranslate as="span" className="font-mono text-xs">
                      {line.accountCode ?? line.accountId}
                    </NoTranslate>
                  </TableCell>
                  <TableCell>
                    <NoTranslate as="span">
                      <Badge
                        variant={
                          line.direction === "DEBIT" ? "outline" : "secondary"
                        }
                      >
                        {line.direction}
                      </Badge>
                    </NoTranslate>
                  </TableCell>
                  <TableCell className="text-right">
                    <NoTranslate as="span" className="font-mono text-sm">
                      {formatMinorAmount(line.amountMinor, line.currency)}
                    </NoTranslate>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
